import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";

import { PageHeader } from "../../../ui";
import {
  getIESConnectUrl,
  getIESEnabled,
  getIESStatus,
  runIESSync,
  setIESEnabled,
  type IESEnabledInfo,
  type IESStatus,
  type IESSyncResult,
} from "../../../services/api/iesService";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

// Admin page for the Intuit Enterprise Suite integration: which backend the
// portal runs on, the state of every connected realm, and the actions an
// admin needs (sync now, connect a new realm, copy the callback URLs).
const Integrations = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [status, setStatus] = useState<IESStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResults, setSyncResults] = useState<IESSyncResult[] | null>(null);
  const [toggleInfo, setToggleInfo] = useState<IESEnabledInfo | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await getIESStatus());
      setToggleInfo(await getIESEnabled());
    } catch (err: any) {
      setError(err?.message || "Failed to load the integration status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRunSync = async () => {
    setSyncing(true);
    setSyncResults(null);
    try {
      const res = await runIESSync();
      setSyncResults(res?.results || []);
      const failed = (res?.results || []).filter((r) => !r.ok).length;
      enqueueSnackbar(
        failed === 0
          ? "IES sync completed."
          : `IES sync finished with ${failed} realm error(s).`,
        { variant: failed === 0 ? "success" : "warning" }
      );
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Failed to run the IES sync.", {
        variant: "error",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleIES = async () => {
    const next = !(toggleInfo?.enabled ?? status?.iesEnabled ?? false);
    setSwitching(true);
    try {
      await setIESEnabled(next);
      enqueueSnackbar(
        next
          ? "IES ingestion enabled — Intuit invoices will sync from now on."
          : "IES ingestion disabled. BILL is unaffected.",
        { variant: "success" }
      );
      setConfirmOpen(false);
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Failed to change the IES setting.", {
        variant: "error",
      });
    } finally {
      setSwitching(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const info = await getIESConnectUrl();
      window.open(info.authorizeUrl, "_blank", "noopener,noreferrer");
      enqueueSnackbar(
        "Consent opened in a new tab. Approve access to link the realm.",
        { variant: "info" }
      );
    } catch (err: any) {
      enqueueSnackbar(
        err?.message || "Could not build the consent URL. Check the app keys.",
        { variant: "error" }
      );
    } finally {
      setConnecting(false);
    }
  };

  const copy = (value: string, label: string) => {
    navigator.clipboard?.writeText(value);
    enqueueSnackbar(`${label} copied.`, { variant: "success" });
  };

  const isIES = toggleInfo?.enabled ?? status?.iesEnabled ?? false;
  const destinations = {
    webhook: `${window.location.origin.replace(
      /:\/\/[^.]*/,
      "://ppp-api"
    )}/webhooks/intuit`,
  };

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Integrations"
        summary="The accounting books the portal reads, and the Intuit realm it syncs."
        actions={
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={load}
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh"}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={handleRunSync}
              disabled={syncing || !isIES}
            >
              {syncing ? "Syncing…" : "Run IES sync"}
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>
          {error}
        </Alert>
      )}

      {status?.reason && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 0 }}>
          {status.reason}
        </Alert>
      )}

      {/* Provider + configuration */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 0, mb: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--tpc-600)", mr: 1 }}>
            Billing providers
          </Typography>
          <Chip
            label="BILL: always on"
            color="default"
            size="small"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={isIES ? "IES: enabled" : "IES: disabled"}
            color={isIES ? "info" : "default"}
            size="small"
            variant={isIES ? "filled" : "outlined"}
            sx={{ fontWeight: 700 }}
          />
          <Chip
            label={`env: ${status?.environment || "—"}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={status?.configured ? "configured" : "not configured"}
            color={status?.configured ? "success" : "warning"}
            size="small"
            variant="outlined"
          />
          <Button
            size="small"
            variant="outlined"
            disabled={!status?.configured}
            onClick={() => setConfirmOpen(true)}
            sx={{ ml: "auto", borderRadius: 0, }}
          >
            {isIES ? "Disable IES" : "Enable IES"}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: 13 }}>
          Both providers run side by side: BILL continues to work exactly as it does
          today, and enabling IES adds Intuit invoices (kept in step by webhooks and a
          scheduled sync) and routes Intuit invoices to QuickBooks Payments. The setting
          applies immediately — no restart, and no environment variable to change.
        </Typography>
        {!isIES && (
          <Alert severity="info" sx={{ mt: 1.5, borderRadius: 0 }}>
            IES is currently disabled. BILL invoices, clients and payments are unaffected;
            enable IES to ingest Intuit data.
          </Alert>
        )}
      </Paper>

      {/* Actions */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 0, mb: 2 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--tpc-600)", mb: 1.5 }}>
          Connect a realm
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mb: 1.5 }}>
          Opens Intuit's consent screen in a new tab. After approval the realm's tokens
          are stored automatically and the realm registers itself — it starts syncing as
          soon as IES is enabled, with no configuration change.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<LinkIcon />}
            onClick={handleConnect}
            disabled={connecting}
            sx={{ borderRadius: 0, }}
          >
            {connecting ? "Preparing..." : "Connect realm"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() =>
              copy(
                status?.redirectUri || `${window.location.origin}/ies/oauth/callback`,
                "Redirect URI"
              )
            }
            sx={{ borderRadius: 0, }}
          >
            Copy redirect URI
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => copy(destinations.webhook, "Webhook URL")}
            sx={{ borderRadius: 0, }}
          >
            Copy webhook URL
          </Button>
        </Box>
      </Paper>

      {/* Realms */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 0 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--tpc-600)", mb: 1.5 }}>
          Connected realms
        </Typography>
        {loading && !status ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Realm</TableCell>
                <TableCell>Tokens</TableCell>
                <TableCell>Access expires</TableCell>
                <TableCell>Refresh expires</TableCell>
                <TableCell>Last sync</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(status?.realms || []).map((realm) => (
                <TableRow key={realm.realmId}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>
                    {realm.realmId}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={realm.hasTokens ? "connected" : "no tokens"}
                      size="small"
                      color={realm.hasTokens ? "success" : "warning"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {realm.accessTokenExpiresAt
                      ? new Date(realm.accessTokenExpiresAt).toLocaleString()
                      : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {realm.refreshTokenExpiresAt
                      ? new Date(realm.refreshTokenExpiresAt).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {realm.lastSyncAt
                      ? new Date(realm.lastSyncAt).toLocaleString()
                      : "never"}
                  </TableCell>
                </TableRow>
              ))}
              {(status?.realms || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No realms configured. Set <code>IES_REALM_IDS</code> on the API.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {syncResults && syncResults.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
              Last sync run
            </Typography>
            {syncResults.map((result) => (
              <Box
                key={result.realmId}
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Chip
                  size="small"
                  label={result.ok ? "ok" : "failed"}
                  color={result.ok ? "success" : "error"}
                  variant="outlined"
                />
                <Typography sx={{ fontSize: 13, fontFamily: "monospace" }}>
                  {result.realmId}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                  {result.ok
                    ? `${(result.durationMs / 1000).toFixed(1)}s`
                    : result.error}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </Paper>

      {/* Provider switch confirmation */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>
          {isIES ? "Disable IES ingestion" : "Enable IES ingestion"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, fontSize: 13 }}>
            {isIES
              ? "Intuit invoices stop syncing (webhooks and the scheduled job) and Intuit invoices can no longer be paid through the portal. Existing data stays in place, and BILL is unaffected."
              : "Intuit invoices start syncing (webhooks + scheduled job) and can be paid through QuickBooks Payments. BILL keeps working exactly as it does today."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleToggleIES}
            disabled={switching}
            sx={{ borderRadius: 0, }}
          >
            {switching ? "Saving..." : isIES ? "Disable IES" : "Enable IES"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Integrations;
